import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logClickEvent } from '../redux/UsageMetricsReducer/usageMetricsSlice';
import { selectUserDetails } from '../redux/UserReducer/userSlice';
import { isInternalEnterpriseUrl } from './appUtils';

/**
 * Shared function signature for the click handler returned by useClickHandler.
 * Used in MuiDataGrid and GridRenderContext to keep the type in sync.
 */
export type ClickHandler = (
  clickLogged: string,
  options?: { url?: string | null; urlTarget?: string; callback?: () => void },
) => void;

/**
 * Custom hook that creates a click logger function for usage metrics.
 *
 * @param pageNm - The page name where clicks will be logged (e.g., "Header", "Dashboard")
 * @param tabNm - Optional tab name if clicks occur within a specific tab
 *
 * @returns A logClick function that accepts the action/button name being logged
 *
 * @example
 * ```tsx
 * const logClick = useLogClick('Header');
 * logClick('My Retail Growth Plan');
 * ```
 */
export const useLogClick = (pageNm: string, tabNm: string | null = null) => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(selectUserDetails);

  // useCallback recreates the function when userDetails changes, ensuring we always log with current user data.
  return useCallback(
    (clickLogged: string) => {
      if (!userDetails) {
        console.warn('[RADUsageMetrics] Unable to log user action: session data unavailable', {
          pageNm,
          tabNm,
          clickLogged,
        });
        return;
      }

      dispatch(
        logClickEvent({
          storeNum: userDetails.currentStoreNumber,
          userId: userDetails.userId,
          clickLogged,
          pageNm,
          tabNm,
          corporateFl: userDetails.userType === 'Corporate' ? 'Y' : 'N',
          apimSubscriptionKey: userDetails.apimSubscriptionKey,
        }),
      );
    },
    [dispatch, userDetails, pageNm, tabNm],
  );
};

/**
 * Custom hook that creates a reusable click handler for logging events and performing actions.
 * This utility simplifies handling clicks that may open URLs, execute callbacks, or both.
 *
 * **Internal URL Optimization:**
 * - Internal enterprise URLs (BASE_URL_WFC, BASE_URL_ACENET, BASE_URL_RETAILER) open without
 *   'noopener,noreferrer' to avoid redirect latency and eliminate the 'about:blank' loading delay
 * - External URLs retain 'noopener,noreferrer' for security
 *
 * @param pageNm - The page name where clicks will be logged (e.g., "Header", "Operations")
 * @param tabNm - Optional tab name if clicks occur within a specific tab
 *
 * @returns A handleClick function that accepts:
 *   - clickLogged: The action name to log
 *   - options: Optional configuration object containing:
 *     - url: URL to open
 *     - urlTarget: Target for window.open (default: '_blank' for new tab)
 *     - callback: Custom function to execute
 *
 * @example
 * ```tsx
 * const handleAndLogClick = useClickHandler('Operations', 'Inventory');
 *
 * // Just log the click
 * handleAndLogClick('User Guide');
 *
 * // Log and open internal URL (fast, no about:blank delay)
 * handleAndLogClick('My Retail Growth Plan', { url: 'https://qaacenet.npaceservices.com/plan' });
 *
 * // Log and open external URL (secure, with noopener/noreferrer)
 * handleAndLogClick('External Link', { url: 'https://example.com' });
 *
 * // Log and open URL in same tab. Can be overriden to other targets
 * handleAndLogClick('Navigation', { url: 'https://example.com', urlTarget: '_self' });
 *
 * // Log and execute callback
 * handleAndLogClick('View Info', { callback: () => setDrawerOpen(true) });
 *
 * // Log, execute callback, and open URL
 * handleAndLogClick('Download Report', {
 *   callback: () => console.log('Downloading...'),
 *   url: 'https://example.com/report.pdf'
 * });
 * ```
 */
export const useClickHandler = (pageNm: string, tabNm: string | null = null) => {
  const logClick = useLogClick(pageNm, tabNm);

  return useCallback(
    (
      clickLogged: string,
      options?: {
        url?: string | null;
        urlTarget?: string;
        callback?: () => void;
      },
    ) => {
      // Execute custom callback if provided
      if (options?.callback) {
        options.callback();
      }

      // Navigate to URL if provided and not empty
      if (options?.url && options.url.trim() !== '') {
        // Conditionally apply noopener/noreferrer based on URL type
        // Internal enterprise URLs: omit to avoid redirect latency
        // External URLs: include for security
        const windowFeatures = isInternalEnterpriseUrl(options.url)
          ? undefined
          : 'noopener,noreferrer';
        window.open(options.url, options.urlTarget ?? '_blank', windowFeatures);
      }

      // Log the click event
      logClick(clickLogged);
    },
    [logClick],
  );
};
